/**
 * ticketCierreTablaRejilla.ts — Estilos compartidos para tablas tipo “cuadro” en el ticket de cierre.
 *
 * QUÉ HACE:
 *  - Centraliza clases Tailwind de rejilla mono, celdas centradas, bordes verticales (`border-r`)
 *    y líneas horizontales internas (`<hr>`), alineadas con el trazo `border-gray-800` del ticket.
 *
 * QUÉ INVOCA: nada (solo constantes).
 *
 * QUÉ EXPORTA: constantes de layout para `CierreTicketPreview` y otras vistas de ticket de caja.
 *
 * QUÉ NO DEBE HACER:
 *  - No importar React ni componentes.
 *  - No acoplarse a Firebase ni a datos reales.
 *
 * REGLAS:
 *  - Usar `[grid-column:1/-1]` en `<hr>` y bloques ancho completo para que sirva con N columnas
 *    sin duplicar clases `col-span-*` por tabla.
 *  - Separación vertical: solo `border-r` en las celdas (evita columna 1px + gap = doble trazo).
 */

/** Contenedor base mono compacto (creditos, abonos a créditos, etc.). */
export const TICKET_CIERRE_TABLA_MONO_BASE =
  "w-full min-w-0 font-mono text-[7px] sm:text-[8px] leading-snug text-gray-900";

/** Rejilla sin gap horizontal; filas estiradas para que `border-r` llegue al borde de la fila. */
export const TICKET_CIERRE_TABLA_GRID =
  "grid w-full min-w-0 items-stretch gap-x-0 [column-gap:0] border border-solid border-gray-800";

export const TICKET_CIERRE_TABLA_CELDA_PAD = "px-2 sm:px-2.5 py-1.5 sm:py-2";

/** Cabecera de columna: centrada, mayúsculas, gris etiqueta. */
export const TICKET_CIERRE_TABLA_HEAD_CELL = [
  TICKET_CIERRE_TABLA_CELDA_PAD,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-center justify-center text-center font-bold uppercase tracking-wide text-gray-600 text-[7px] sm:text-[8px]",
].join(" ");

/** Celda de dato: centrada (misma lógica que tabla creditos sesión). */
export const TICKET_CIERRE_TABLA_DATA_CELL = [
  TICKET_CIERRE_TABLA_CELDA_PAD,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-center justify-center text-center",
].join(" ");

/** Padding reducido (p. ej. tabla abonos a créditos en ticket). */
export const TICKET_CIERRE_TABLA_CELDA_PAD_COMPACTA =
  "px-1 sm:px-1.5 py-0.5 sm:py-1";

export const TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA = [
  TICKET_CIERRE_TABLA_CELDA_PAD_COMPACTA,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-start justify-center text-center font-bold uppercase tracking-wide text-gray-600 text-[7px] sm:text-[8px] leading-tight whitespace-normal break-words",
].join(" ");

export const TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA = [
  TICKET_CIERRE_TABLA_CELDA_PAD_COMPACTA,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-start justify-center text-center leading-tight whitespace-normal break-words",
].join(" ");

/** Fila “Método | Ingreso”: etiqueta izquierda (con `COL_SEP`). */
export const TICKET_CIERRE_TABLA_HEAD_METODO_IZQ = [
  TICKET_CIERRE_TABLA_CELDA_PAD_COMPACTA,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-start justify-start text-left font-bold uppercase tracking-wide text-gray-600 text-[7px] sm:text-[8px] leading-tight whitespace-normal break-words",
].join(" ");

export const TICKET_CIERRE_TABLA_HEAD_METODO_DER = [
  TICKET_CIERRE_TABLA_CELDA_PAD_COMPACTA,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-start justify-end text-right font-bold uppercase tracking-wide text-gray-600 text-[7px] sm:text-[8px] leading-tight tabular-nums whitespace-normal break-words",
].join(" ");

export const TICKET_CIERRE_TABLA_DATA_METODO_IZQ = [
  TICKET_CIERRE_TABLA_CELDA_PAD_COMPACTA,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-start justify-start text-left leading-tight text-gray-900 whitespace-normal break-words",
].join(" ");

export const TICKET_CIERRE_TABLA_DATA_METODO_DER = [
  TICKET_CIERRE_TABLA_CELDA_PAD_COMPACTA,
  "ticket-cierre-grid-cell flex w-full min-h-0 min-w-0 items-start justify-end text-right leading-tight tabular-nums font-bold text-gray-900",
].join(" ");

/** Borde derecho entre columnas (un solo trazo). */
export const TICKET_CIERRE_TABLA_COL_SEP =
  "border-r border-solid border-gray-800 box-border";

/** Dos columnas: método (crece) | ingreso (mínimo ancho montos). */
export const TICKET_CIERRE_TABLA_COLUMNAS_METODO_INGRESOS = {
  gridTemplateColumns: "minmax(0,1fr) minmax(4.5rem,auto)",
} as const;

/**
 * `<hr>` a ancho completo de la rejilla (sin `ticket-divider` ni márgenes negativos).
 * Va pegado entre filas; no repetir dos `<hr>` seguidos entre bloques.
 */
export const TICKET_CIERRE_TABLA_HR_FILA = [
  "col-start-1 [grid-column:1/-1]",
  "m-0 h-0 w-full min-w-0 shrink-0 border-0 border-b border-solid border-gray-800",
].join(" ");

/** Separador entre tarjetas de propina por empleado (un trazo, mismo peso visual que tablas). */
export const TICKET_CIERRE_TABLA_HR_PROPINA_EMPLEADO = [
  "col-start-1 [grid-column:1/-1]",
  "m-0 h-0 w-full min-w-0 shrink-0 border-0 border-b border-solid border-gray-500",
].join(" ");

/** Bloque opcional bajo una fila (detalle nota/productos), ancho completo. */
export const TICKET_CIERRE_TABLA_BLOQUE_DETALLE = [
  "col-start-1 w-full min-w-0 [grid-column:1/-1]",
  "border-t border-gray-800 bg-gray-50/60 px-2.5 py-2 sm:px-3 sm:py-2.5 space-y-1.5 box-border",
].join(" ");

/** Mismo bloque detalle con menos padding (tabla creditos compacta alineada a abonos). */
export const TICKET_CIERRE_TABLA_BLOQUE_DETALLE_COMPACTA = [
  "col-start-1 w-full min-w-0 [grid-column:1/-1]",
  "border-t border-gray-800 bg-gray-50/60 px-1.5 py-1.5 sm:px-2 sm:py-2 space-y-1 box-border leading-tight",
].join(" ");

/** Columnas tabla creditos (sesión). */
export const TICKET_CIERRE_TABLA_COLUMNAS_CREDITOS_SESION = {
  gridTemplateColumns: "minmax(0,1.15fr) auto auto minmax(0,1fr)",
} as const;

/** Columnas tabla abonos a créditos (cobros); primera columna solo hora (más estrecha). */
export const TICKET_CIERRE_TABLA_COLUMNAS_ABONOS_CREDITOS = {
  gridTemplateColumns:
    "minmax(2.25rem,auto) minmax(0,1fr) minmax(2.35rem,auto) minmax(1.35rem,auto) minmax(0,1fr)",
} as const;

/** Otros movimientos de caja: Tipo | Monto | Hora | Responsable. */
export const TICKET_CIERRE_TABLA_COLUMNAS_MOV_OTROS_CAJA = {
  gridTemplateColumns:
    "minmax(3.25rem,auto) minmax(4.25rem,auto) minmax(2.25rem,auto) minmax(0,1fr)",
} as const;

/** Separador horizontal a ancho del ticket (equiv. `sectionSub` / `minorSep` del ASCII). */
export const TICKET_CIERRE_HR_SECCION = [
  "ticket-divider -mx-3 sm:-mx-6",
  "my-0 h-0 w-full shrink-0 border-0 border-b border-solid border-gray-800",
].join(" ");

/** Título centrado entre dos `TICKET_CIERRE_HR_SECCION` (p. ej. resumen operativo, propinas). */
export const TICKET_CIERRE_SUBSECTION_TITLE = [
  "text-[8px] sm:text-[9px] font-bold uppercase text-center text-gray-900",
  "py-0.5 sm:py-1 leading-tight",
].join(" ");

/** Fila meta «Etiqueta: … valor» alineada como `metaRow` del ticket ASCII. */
export const TICKET_CIERRE_META_ROW = [
  "flex w-full min-w-0 justify-between items-start gap-2",
  "text-[8px] sm:text-[9px] text-gray-900 leading-tight py-0",
].join(" ");

/** Separador `====` de bloque principal (equiv. `sectionMajor` / `majorSep`). */
export const TICKET_CIERRE_HR_SECCION_MAJOR = [
  "ticket-divider -mx-3 sm:-mx-6",
  "my-0 h-0 w-full shrink-0 border-0 border-b-2 border-solid border-gray-800",
].join(" ");

/** Título oficial dentro de una celda completamente cerrada. */
export const TICKET_CIERRE_SECTION_TITLE = [
  TICKET_CIERRE_SUBSECTION_TITLE,
  "border border-b-0 border-solid border-gray-800 px-2 sm:px-2.5 py-1.5 sm:py-2",
].join(" ");

/** Etiqueta interna de sub-bloque (p. ej. «Recaudo del turno — Efectivo»). */
export const TICKET_CIERRE_SUBSECTION_LABEL = [
  "text-[8px] sm:text-[9px] font-bold uppercase text-center text-gray-900",
  "py-0.5 sm:py-1 leading-tight",
].join(" ");

/** Cuatro columnas: empleado | ventas | # ventas | propinas. */
export const TICKET_CIERRE_TABLA_COLUMNAS_RENDIMIENTO_EMPLEADO = {
  gridTemplateColumns:
    "minmax(0,1.2fr) minmax(3.5rem,auto) minmax(2rem,auto) minmax(3.5rem,auto)",
} as const;
